/**
 * CourseBottom Component
 *
 * Displays the bottom section of the course page, including course includes, content, additional materials, and reviews.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Uses Redux selector to get course data
 * - Handles review form state with local state
 * - Passes props to CourseIncludes, CourseContent, CourseAditionalMaterial, and ReviewsCourse
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.ltiData - LTI data for course integration
 * @returns {JSX.Element} The rendered course bottom section
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CourseIncludes from './CourseIncludes/CourseIncludes';
import CourseContent from './CourseContent/CourseContent';
import CourseAditionalMaterial from './CourseAditionalMaterial/CourseAditionalMaterial';
import ReviewsCourse from './ReviewsCourse/ReviewsCourse';
import classes from './CourseBottom.module.css';

const CourseBottom = props => {
  const [showWriteReview, setShowWriteReview] = useState(false);
  const dataCourse = useSelector(state => state.courseInfo);

  /**
   * Handler to show/hide the review form
   * Passed as prop to ReviewsCourse
   */
  const showWriteReviewHandler = () => {
    setShowWriteReview(!showWriteReview);
  };

  return (
    <aside className={classes.courseBottomContainer}>
      {/* CourseIncludes: passes access, LTI, course data, lessons, materials, activities, and hours */}
      <>
        <CourseIncludes
          accessCourse={dataCourse.accessCourse} // Prop passed: access control
          ltiData={props.ltiData} // Prop passed: LTI data
          dataCourse={dataCourse} // Prop passed: full course data
          lessons={
            dataCourse.lessonsCourse.lessonExtraInfo.lessons.total
          } // Prop passed: total lessons
          aditionalMaterial={
            dataCourse.lessonsCourse.lessonExtraInfo
              .lessonAdditionalMaterial.additionalMaterial
          } // Prop passed: additional materials
          activities={
            dataCourse.lessonsCourse.lessonExtraInfo.lessonActivities
              .activities
          } // Prop passed: activities
          hrs={
            dataCourse.lessonsCourse.lessonExtraInfo.lessons
              .seconds /
            60 /
            60
          } // Prop passed: total hours
        />
        {/* CourseContent: passes lessons, access, and user track */}
        <CourseContent
          lessons={dataCourse.lessonsCourse} // Prop passed: lessons data
          accessCourse={dataCourse.accessCourse} // Prop passed: access control
          userTrack={dataCourse.userAditionalTrack.userTrack} // Prop passed: user progress tracking
        />
        {/* CourseAditionalMaterial: only rendered if there are files */}
        {dataCourse.data.archivos.length > 0 && (
          <CourseAditionalMaterial
            aditionalMaterial={dataCourse.data.archivos} // Prop passed: additional files
            accessCourse={dataCourse.accessCourse} // Prop passed: access control
          />
        )}
      </>
      {/* ReviewsCourse: only rendered if reviews are enabled */}
      {dataCourse.aditionalData.reviews === 1 && (
        <ReviewsCourse
          dataCourse={dataCourse.data} // Prop passed: course data for reviews
          courseReviews={dataCourse.courseReviews} // Prop passed: reviews data
          showWriteReviewHandler={showWriteReviewHandler} // Prop passed: handler to toggle review form
          showWriteReview={showWriteReview} // Prop passed: review form visibility
          accessCourse={dataCourse.accessCourse} // Prop passed: access control
        />
      )}
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseBottom.propTypes = {
  /**
   * LTI data for course integration
   * Passed as prop to CourseIncludes
   */
  ltiData: PropTypes.object
};

CourseBottom.defaultProps = {
  ltiData: {}
};

export default CourseBottom;
